import {
  socketService,
  PlayerStats,
  GameRoom,
} from '../services/SocketService';
import { LobbyScreen } from '../components/LobbyScreen';
import { GameplayScreen } from '../components/GameplayScreen';

export class MultiplayerUI {
  private container: HTMLElement;
  private currentScreen: 'lobby' | 'gameplay' = 'lobby';
  private lobbyScreen: LobbyScreen;
  private gameplayScreen: GameplayScreen | null = null;
  private onlineRefreshIntervalId: number | null = null;
  private pendingChallenges: Set<string> = new Set();

  constructor(container: HTMLElement) {
    this.container = container;

    this.lobbyScreen = new LobbyScreen(container, {
      onCreateGame: (gameName) => this.handleCreateGame(gameName),
      onJoinGame: (gameId) => this.handleJoinGame(gameId),
      onLogout: () => this.handleLogout(),
    });

    this.initialize();
  }

  private async initialize(): Promise<void> {
    socketService.connect();
    this.setupSocketListeners();

    // Show lobby screen
    this.switchToLobby();
    // Auto-login if gametag exists in localStorage. Await so we have current player/socket info
    // available before requesting online players (prevents showing self until refresh).
    await this.attemptAutoLogin();

    // Load initial data after login attempt so filtering works on first render
    socketService.getOnlinePlayers();
    socketService.getLeaderboard(10);

    // Listen for manual login events (from LobbyScreen) to start polling
    window.addEventListener('gomoku:login-success', () => {
      // Start polling now that user is logged in via UI
      this.startOnlinePlayersPollingIfNeeded();
    });
  }

  private async attemptAutoLogin(): Promise<void> {
    const savedGametag = localStorage.getItem('gomoku_gametag');

    if (savedGametag) {
      try {
        const player = await socketService.login(savedGametag);
        this.lobbyScreen.setCurrentPlayer(player);
        this.lobbyScreen.showPlayerSection();
        console.info(`Auto-logged in as: ${savedGametag}`);
        // If auto-login succeeded, start polling online players
        this.startOnlinePlayersPollingIfNeeded();
      } catch (error) {
        localStorage.removeItem('gomoku_gametag');
        console.info('Auto-login failed, gametag cleared');
      }
    }
  }

  private setupSocketListeners(): void {
    socketService.onGameList((games) => {
      this.lobbyScreen.setAvailableGames(games);
    });

    socketService.onLeaderboard((leaderboard) => {
      this.lobbyScreen.setLeaderboard(leaderboard);
    });

    socketService.onGameStarted((game) => {
      this.startGame(game);
    });

    socketService.onMoveMade((data) => {
      if (
        this.gameplayScreen &&
        data.gameId === this.gameplayScreen.getCurrentGame().id
      ) {
        if (data.playerId !== socketService.socketId) {
          this.gameplayScreen.applyMove(data.move.row, data.move.col);
        }

        this.gameplayScreen.setIsMyTurn(
          data.playerId !== socketService.socketId
        );
      }
    });

    socketService.onGameEnded((data) => {
      if (
        this.gameplayScreen &&
        data.gameId === this.gameplayScreen.getCurrentGame().id
      ) {
        this.gameplayScreen.stopChessClock();
        // Record local match history if gameplayScreen is present and we have data
        try {
          const game = this.gameplayScreen.getCurrentGame();
          const mySocketId = socketService.socketId;

          // Determine which player index corresponds to me. Be defensive: if not found, fallback
          // to assuming I'm player 0.
          let myIndex = game.players.findIndex(
            (p) => p.socketId === mySocketId
          );
          if (myIndex === -1) myIndex = 0;
          const opponent = game.players[myIndex];
          const opponentName = opponent?.name || 'Opponent';

          // Pick the correct elo change field based on myIndex. Use nullish coalescing to
          // handle missing values gracefully.
          const myEloChange =
            myIndex === 0
              ? data.player1EloChange ?? 0
              : data.player2EloChange ?? 0;

          const result = !data.winnerId
            ? 'draw'
            : data.winnerId === mySocketId
            ? 'win'
            : 'loss';

          // Append to local history in the gameplay screen
          this.gameplayScreen.addLocalMatchToHistory(
            opponentName,
            result as any,
            myEloChange
          );
        } catch (err) {
          /* ignore */
        }
        this.handleGameEnd(data);
        // Highlight winning line (if engine exposes it)
        try {
          const engine = this.gameplayScreen.getEngine();
          const winning = engine.getWinningLine
            ? engine.getWinningLine()
            : null;
          if (winning && winning.length > 0) {
            this.gameplayScreen.highlightWinningLine(winning as any);
          }
        } catch (err) {
          /* ignore if not available */
        }
      }
    });

    socketService.onPlayerJoined((data) => {
      if (
        this.gameplayScreen &&
        data.game.id === this.gameplayScreen.getCurrentGame().id
      ) {
        this.lobbyScreen.showMessage(
          `${data.player.name} joined! Game starting...`
        );
      }
    });

    socketService.onPlayerLeft((data) => {
      if (
        this.gameplayScreen &&
        data.gameId === this.gameplayScreen.getCurrentGame().id
      ) {
        alert('Opponent left the game. You win by forfeit!');
        this.switchToLobby();

        setTimeout(() => {
          socketService.getLeaderboard(10);
          socketService.getOnlinePlayers();
        }, 1000);
      }
    });

    socketService.onOpponentExitedLobby((data) => {
      if (
        this.gameplayScreen &&
        data.gameId === this.gameplayScreen.getCurrentGame().id
      ) {
        alert('Opponent returned to lobby.');
        this.switchToLobby();

        setTimeout(() => {
          socketService.getLeaderboard(10);
          socketService.getGames();
        }, 1000);
      }
    });

    socketService.onRematchRequested((data) => {
      if (this.gameplayScreen) {
        this.gameplayScreen.showRematchRequest(
          data.requesterName,
          data.requesterId,
          () => {
            socketService.acceptRematch(data.requesterId);
            if (this.gameplayScreen) {
              this.gameplayScreen.showRematchStatus('⏳ Starting rematch...');
            }
          },
          () => {
            socketService.declineRematch(data.requesterId);
            setTimeout(() => {
              this.switchToLobby();
            }, 500);
          }
        );
      }
    });

    socketService.onRematchAccepted((data) => {
      // Show a short message indicating who will go first on the rematch
      try {
        const mySocketId = socketService.socketId;
        const player1Socket = data.game.players?.[0]?.socketId;
        const rematchMsg =
          player1Socket === mySocketId
            ? 'Rematch accepted — You go first'
            : 'Rematch accepted — Opponent goes first';
        if (this.gameplayScreen) {
          this.gameplayScreen.showRematchStatus(rematchMsg);
        }
      } catch (err) {
        // ignore
      }
      // Switch to gameplay for the new game
      this.startGame(data.game);
    });

    socketService.onRematchDeclined(() => {
      if (this.gameplayScreen) {
        this.gameplayScreen.showRematchStatus('❌ Opponent declined');
      }
      setTimeout(() => {
        this.switchToLobby();
      }, 2000);
    });

    // Online players list handler (render into LobbyScreen's right panel)
    socketService.onOnlinePlayers((players) => {
      // If LobbyScreen is rendered, the DOM contains #online-players
      const onlineContainer = document.querySelector('#online-players');
      if (!onlineContainer) return;
      if (players.length === 0) {
        onlineContainer.innerHTML =
          '<p class="no-players">No players online</p>';
        return;
      }

      const mySocketId = socketService.socketId;
      const myDbId = this.lobbyScreen.getCurrentPlayer()?.id;
      const myUsername = this.lobbyScreen.getCurrentPlayer()?.username;
      // Filter out self (match by socket id, DB player id or username/gametag)
      const visible = players.filter((p) => {
        if (p.socketId === mySocketId) return false;
        if (myDbId && p.playerId === myDbId) return false;
        if (myUsername && p.username === myUsername) return false;
        return true;
      });

      onlineContainer.innerHTML = visible
        .map((p) => {
          const isSelf = p.socketId === mySocketId;
          const disabled = p.inGame || isSelf ? 'disabled' : '';
          const label = p.inGame ? 'In Game' : isSelf ? 'You' : 'Challenge';
          return `
          <div class="online-player" data-socket="${p.socketId}">
            <span class="player-name">${p.username}</span>
            <div class="player-action">
              ${
                isSelf
                  ? `<span class="you-label">${label}</span>`
                  : `<button class="challenge-btn" data-socket="${p.socketId}" ${disabled}>${label}</button>`
              }
            </div>
          </div>
        `;
        })
        .join('');

      // Attach click listeners only for challenge buttons (exclude self)
      onlineContainer.querySelectorAll('.challenge-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const sock = (e.currentTarget as HTMLElement).dataset.socket;
          if (!sock) return;
          if (!confirm('Send challenge to this player?')) return;

          // Disable the button and mark as pending
          const buttonEl = e.currentTarget as HTMLButtonElement;
          buttonEl.disabled = true;
          buttonEl.textContent = 'Pending...';
          this.pendingChallenges.add(sock);

          socketService.challengePlayer(sock);
        });
      });
    });

    // Incoming challenge handler - show in-app prompt and await user decision
    socketService.onIncomingChallenge(async (data) => {
      const { requesterSocketId, requesterName } = data;
      console.debug &&
        console.debug('[MultiplayerUI] incoming-challenge handler fired', data);
      try {
        // Use LobbyScreen's in-app prompt to ensure user explicitly accepts/declines
        const accept = await this.lobbyScreen.promptChallenge(
          requesterName || 'Opponent'
        );
        if (accept) {
          console.info(
            '[MultiplayerUI] accepting challenge from',
            requesterSocketId
          );
          socketService.acceptChallenge(requesterSocketId);
        } else {
          console.info(
            '[MultiplayerUI] declining challenge from',
            requesterSocketId
          );
          socketService.declineChallenge(requesterSocketId);
        }
      } catch (err) {
        console.error('Error handling incoming challenge', err);
      }
    });

    socketService.onChallengeSent((data) => {
      this.lobbyScreen.showMessage('Challenge sent');
    });

    // When a challenge is declined (or timed out), re-enable the button and notify user
    socketService.onChallengeDeclined((data: any) => {
      // server may send different shapes, try multiple keys
      const targetId =
        data.targetSocketId || data.requesterSocketId || data.requesterSocketId;
      if (targetId && this.pendingChallenges.has(targetId)) {
        this.pendingChallenges.delete(targetId);
        const btn = document.querySelector(
          `.challenge-btn[data-socket="${targetId}"]`
        ) as HTMLButtonElement;
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Challenge';
        }
      }
      const reason = data?.reason ? ` (${data.reason})` : '';
      this.lobbyScreen.showMessage('Challenge declined' + reason);
    });

    // When a challenge is cancelled (requester disconnected), re-enable UI
    socketService.onChallengeCancelled((data: any) => {
      const requesterId =
        data.requesterSocketId || data.requesterId || data.requester;
      if (requesterId && this.pendingChallenges.has(requesterId)) {
        this.pendingChallenges.delete(requesterId);
        const btn = document.querySelector(
          `.challenge-btn[data-socket="${requesterId}"]`
        ) as HTMLButtonElement;
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Challenge';
        }
      }
      this.lobbyScreen.showMessage('Challenge cancelled');
    });
  }

  private async handleCreateGame(gameName: string): Promise<void> {
    const currentPlayer = this.lobbyScreen.getCurrentPlayer();
    if (!currentPlayer) return;

    try {
      const game = await socketService.createGame(
        currentPlayer.username,
        gameName,
        30
      );
      this.lobbyScreen.showMessage('Game created! Waiting for opponent...');
      // Game will start when opponent joins via onGameStarted event
    } catch (error) {
      this.lobbyScreen.showError(
        error instanceof Error ? error.message : 'Failed to create game'
      );
    }
  }

  private handleJoinGame(gameId: string): void {
    const currentPlayer = this.lobbyScreen.getCurrentPlayer();
    if (!currentPlayer) return;

    socketService
      .joinGame(gameId, currentPlayer.username)
      .then((game) => {
        this.lobbyScreen.showMessage('Joined game! Starting...');
      })
      .catch((error) => {
        this.lobbyScreen.showError(
          error instanceof Error ? error.message : 'Failed to join game'
        );
      });
  }

  private handleLogout(): void {
    localStorage.removeItem('gomoku_gametag');
    socketService.disconnect();
    location.reload();
  }

  private startGame(game: GameRoom): void {
    this.switchToGameplay(game);
  }

  private handleGameEnd(data: {
    winnerId?: string;
    player1EloChange?: number;
    player2EloChange?: number;
  }): void {
    const isWinner = data.winnerId === socketService.socketId;
    const isDraw = !data.winnerId;

    // Store last game result in localStorage for next game
    if (!isDraw) {
      localStorage.setItem(
        'gomoku_last_game_result',
        isWinner ? 'won' : 'lost'
      );
    } else {
      localStorage.setItem('gomoku_last_game_result', 'draw');
    }

    let message = isDraw
      ? "It's a draw!"
      : isWinner
      ? 'You won! 🎉'
      : 'You lost';

    // Determine which player I am based on socket ID
    if (
      this.gameplayScreen &&
      (data.player1EloChange !== undefined ||
        data.player2EloChange !== undefined)
    ) {
      const game = this.gameplayScreen.getCurrentGame();
      const mySocketId = socketService.socketId;
      const isPlayer1 = game.players[0]?.socketId === mySocketId;

      const myEloChange = isPlayer1
        ? data.player1EloChange || 0
        : data.player2EloChange || 0;
      message += `\nELO: ${myEloChange > 0 ? '+' : ''}${myEloChange}`;
    }

    alert(message);

    if (this.gameplayScreen) {
      this.gameplayScreen.switchGameControlsToRematch();
    }

    // Refresh leaderboard
    setTimeout(() => {
      socketService.getLeaderboard(10);
      socketService.getGames();
    }, 2000);
  }

  private switchToLobby(): void {
    this.currentScreen = 'lobby';

    // Cleanup gameplay screen
    if (this.gameplayScreen) {
      this.gameplayScreen.destroy();
      this.gameplayScreen = null;
    }

    // Render lobby
    this.lobbyScreen.render();

    // Restore player section if still logged in
    const savedGametag = localStorage.getItem('gomoku_gametag');
    if (savedGametag) {
      // Re-fetch player data to update stats
      socketService
        .login(savedGametag)
        .then((player) => {
          this.lobbyScreen.setCurrentPlayer(player);
          this.lobbyScreen.showPlayerSection();
        })
        .catch(() => {
          // If login fails, show auth section
          localStorage.removeItem('gomoku_gametag');
          this.lobbyScreen.showAuthSection();
        });
    }

    // Start polling online players every 4 seconds while in the lobby and logged in
    this.startOnlinePlayersPollingIfNeeded();
  }

  private switchToGameplay(game: GameRoom): void {
    this.currentScreen = 'gameplay';
    // Stop polling while in gameplay
    this.stopOnlinePlayersPolling();

    // Create and render gameplay screen
    // Preserve local match history from previous gameplayScreen if available
    const prevHistory = this.gameplayScreen
      ? this.gameplayScreen.getLocalMatchHistory()
      : undefined;
    this.gameplayScreen = new GameplayScreen(this.container, game, {
      onExitToLobby: () => this.switchToLobby(),
    });
    if (prevHistory && this.gameplayScreen) {
      this.gameplayScreen.setLocalMatchHistory(prevHistory);
    }

    this.gameplayScreen.render();
  }

  public destroy(): void {
    if (this.gameplayScreen) {
      this.gameplayScreen.destroy();
    }
    socketService.disconnect();
    this.stopOnlinePlayersPolling();
  }

  private startOnlinePlayersPollingIfNeeded(): void {
    // Only start polling if logged-in (lobbyScreen has a current player)
    if (!this.lobbyScreen.getCurrentPlayer()) return;

    if (this.onlineRefreshIntervalId) return; // already running

    // Use window.setInterval which returns a number in browsers
    this.onlineRefreshIntervalId = window.setInterval(() => {
      socketService.getOnlinePlayers();
    }, 4000) as unknown as number;
  }

  private stopOnlinePlayersPolling(): void {
    if (!this.onlineRefreshIntervalId) return;
    window.clearInterval(this.onlineRefreshIntervalId);
    this.onlineRefreshIntervalId = null;
  }
}
